import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Building2, LogOut } from "lucide-react";
import { useMemberLookup, MemberLookupResult } from "@/hooks/monitor/useMemberLookup";
import { MemberResultModal } from "./MemberResultModal";
import { MemberNotFoundModal } from "./MemberNotFoundModal";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Gym {
  id: string;
  name: string;
  logo_url: string | null;
  address: string;
}

interface ValidationViewProps {
  gym: Gym;
}

export const ValidationView = ({ gym }: ValidationViewProps) => {
  const [dni, setDni] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [showNotFound, setShowNotFound] = useState(false);
  const [result, setResult] = useState<MemberLookupResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { lookup, isLoading, error } = useMemberLookup(gym.id);
  const { signOut } = useAuth();

  // Auto-focus input on mount and maintain focus when clicking empty areas
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    
    input.focus();
    
    const handleBlur = () => {
      // Small delay to avoid conflicts with modals
      setTimeout(() => {
        // Don't refocus if modal is open
        if (showResult || showNotFound) return;
        
        // Don't refocus if the active element is interactive
        const activeElement = document.activeElement;
        const isInteractiveElement = activeElement?.matches(
          'button, a, [role="button"], [tabindex]:not([tabindex="-1"])'
        );
        
        if (!isInteractiveElement) {
          input.focus();
        }
      }, 100);
    };
    
    input.addEventListener('blur', handleBlur);
    return () => input.removeEventListener('blur', handleBlur);
  }, [showResult, showNotFound]);

  // Show toast when there's an error
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Kiosk mode: receive numpad keys from Electron desktop app
  useEffect(() => {
    const kiosk = (window as any).clubbersKiosk;
    if (!kiosk?.onNumpadKey) return;
    const off = kiosk.onNumpadKey((key: string) => {
      if (showResult || showNotFound) return;
      if (key === "BACKSPACE") {
        setDni((d) => d.slice(0, -1));
      } else if (key === "ENTER") {
        setDni((d) => {
          if (d.trim()) runLookup(d);
          return d;
        });
      } else if (/^[0-9]$/.test(key)) {
        setDni((d) => (d.length >= 8 ? d : d + key));
      }
    });
    return () => { if (typeof off === "function") off(); };
  }, [showResult, showNotFound]);

  // Auto-submit when DNI reaches 8 digits
  useEffect(() => {
    if (dni.length === 8 && !isLoading && !showResult && !showNotFound) {
      runLookup(dni);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dni]);

  const runLookup = async (value: string) => {
    if (!value.trim() || isLoading) return;
    const { result: lookupResult, notFound } = await lookup(value);
    if (lookupResult) {
      setResult(lookupResult);
      setShowResult(true);
    } else if (notFound) {
      setShowNotFound(true);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dni.trim() || isLoading) return;
    const { result: lookupResult, notFound } = await lookup(dni);

    if (lookupResult) {
      setResult(lookupResult);
      setShowResult(true);
    } else if (notFound) {
      setShowNotFound(true);
    }
    // If there's an error, the hook sets it and the useEffect shows a toast
  };

  const handleCloseResult = () => {
    setShowResult(false);
    setResult(null);
    setDni("");
    inputRef.current?.focus();
  };

  const handleCloseNotFound = () => {
    setShowNotFound(false);
    setDni("");
    inputRef.current?.focus();
  };

const handleDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers, max 8 digits
    const value = e.target.value.replace(/\D/g, "").slice(0, 8);
    setDni(value);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      {/* Logout button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={signOut}
        className="absolute top-4 right-4"
      >
        <LogOut className="h-4 w-4 mr-2" />
        Salir
      </Button>

      {/* Gym Logo */}
      <div className={`mb-12 transition-all duration-500 ease-out ${isLoading ? 'translate-y-24' : 'translate-y-0'}`}>
        {gym.name.toUpperCase().includes("AMARELO") ? (
          <img
            src="/images/amarelo-gym-monitor-logo.svg"
            alt={gym.name}
            className={`h-96 w-auto object-contain transition-all duration-300 ${
              isLoading 
                ? 'animate-glow-pulse-intense' 
                : 'drop-shadow-[0_0_35px_rgba(255,255,255,0.6)]'
            }`}
          />
        ) : gym.name.toUpperCase().includes("BERROS") ? (
          <img
            src="/images/los-berros-gym-monitor-logo.png"
            alt={gym.name}
            className={`h-[350px] md:h-[400px] lg:h-[450px] w-auto object-contain transition-all duration-300 ${
              isLoading 
                ? 'animate-glow-pulse-intense' 
                : 'drop-shadow-[0_0_45px_rgba(255,50,50,0.7)]'
            }`}
          />
        ) : gym.name.toUpperCase().includes("MASTERGYM") ? (
          <img
            src="/images/mastergym-monitor-logo.png"
            alt={gym.name}
            className={`h-[420px] md:h-[500px] lg:h-[580px] w-auto object-contain transition-all duration-300 ${
              isLoading
                ? 'animate-glow-pulse-intense'
                : 'drop-shadow-[0_0_60px_rgba(163,255,38,0.75)]'
            }`}
          />
        ) : gym.name.toUpperCase().includes("NEW BODY") ? (
          <img
            src="/images/newbody-gym-monitor-logo.png"
            alt={gym.name}
            className={`h-[220px] md:h-[260px] lg:h-[300px] w-auto object-contain transition-all duration-300 ${
              isLoading
                ? 'animate-glow-pulse-intense'
                : 'drop-shadow-[0_0_60px_rgba(139,92,246,0.75)]'
            }`}
          />
        ) : gym.logo_url ? (
          <img
            src={gym.logo_url}
            alt={gym.name}
            className={`h-40 w-40 object-contain transition-all duration-300 ${
              isLoading ? 'animate-glow-pulse-intense' : ''
            }`}
          />
        ) : (
          <div className={`h-40 w-40 rounded-2xl bg-muted flex flex-col items-center justify-center transition-all duration-300 ${
            isLoading ? 'animate-glow-pulse-intense' : ''
          }`}>
            <Building2 className="h-16 w-16 text-muted-foreground mb-2" />
            <span className="text-lg font-semibold text-center px-2">
              {gym.name}
            </span>
          </div>
        )}
      </div>

      {/* DNI Display - números grandes cuando se escriben */}
      <form onSubmit={handleSearch} className="w-full max-w-lg">
        {/* Contenedor con altura fija para los números */}
        <div className={`h-32 flex items-center justify-center min-w-[400px] transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
          <div className="flex items-center">
            {dni.length > 0 && (
              <span className="text-8xl font-mono tracking-[0.3em] text-foreground font-bold">
                {dni}
              </span>
            )}
            {/* Cursor parpadeante */}
            <span className="inline-block w-1 h-20 bg-foreground animate-blink" />
          </div>
        </div>
        
        {/* Input invisible pero funcional */}
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={dni}
          onChange={handleDniChange}
          disabled={isLoading}
          maxLength={8}
          className="sr-only"
          autoComplete="off"
        />
        
        <p className="text-center text-2xl text-muted-foreground mt-8">
          Ingresa el documento {typeof window !== "undefined" && (window as any).clubbersKiosk
            ? <>y la b&uacute;squeda inicia <span className="font-bold text-foreground">autom&aacute;ticamente</span></>
            : <>y presion&aacute; <span className="font-bold text-foreground">Enter</span></>}
        </p>
      </form>

      {/* Result Modal */}
      <MemberResultModal
        open={showResult}
        onClose={handleCloseResult}
        result={result}
      />

      {/* Not Found Modal */}
      <MemberNotFoundModal
        open={showNotFound}
        onClose={handleCloseNotFound}
        dni={dni}
      />
    </div>
  );
};
